import React, { useEffect, useMemo, useState, useRef } from 'react';
import AgriLinkAgriTwinAnalytics from './AgriLinkAgriTwinAnalytics';

// AgriLink_DigitalTwin.jsx
// Mobile-first responsive Digital Twin simulation page for Agri sector
// - Input: Digital Twin Pass Code
// - Select: Commodity Category -> Commodity Example
// - Show Live: starts a real-time simulation of sensor & market data
// Dummy data + simulated realtime updates (setInterval)

export default function AgriLinkDigitalTwin() {
  // Categories & examples (same as AgriCommodity module)
  const categories = [
    { id: 'cereals', name: 'Cereals & Millets', examples: ['Rice', 'Wheat', 'Maize', 'Barley', 'Oats', 'Sorghum', 'Millets'] },
    { id: 'pulses', name: 'Pulses', examples: ['Lentils', 'Chickpeas', 'Pigeon Pea', 'Black Gram', 'Green Gram', 'Kidney Beans'] },
    { id: 'oilseeds', name: 'Oilseeds', examples: ['Groundnut', 'Soybean', 'Mustard', 'Sunflower', 'Sesame'] },
    { id: 'fruits', name: 'Fruits', examples: ['Mango', 'Banana', 'Apple', 'Grapes', 'Pomegranate', 'Papaya'] },
    { id: 'vegetables', name: 'Vegetables', examples: ['Potato', 'Onion', 'Tomato', 'Cauliflower', 'Brinjal', 'Okra'] },
    { id: 'spices', name: 'Spices', examples: ['Turmeric', 'Cumin', 'Black Pepper', 'Cardamom', 'Coriander', 'Clove'] },
    { id: 'plantation', name: 'Plantation Crops', examples: ['Tea', 'Coffee', 'Rubber', 'Cocoa', 'Coconut'] },
    { id: 'commercial', name: 'Commercial / Cash Crops', examples: ['Sugarcane', 'Cotton', 'Tobacco', 'Jute'] },
    { id: 'livestock', name: 'Livestock & Animal', examples: ['Milk', 'Meat', 'Eggs', 'Wool'] },
    { id: 'fisheries', name: 'Fisheries & Aquaculture', examples: ['Shrimp', 'Prawn', 'Rohu', 'Catla', 'Tilapia'] },
    { id: 'forestry', name: 'Forestry', examples: ['Timber', 'Bamboo', 'Honey', 'Resin'] },
    { id: 'fodder', name: 'Fodder & Feed', examples: ['Lucerne', 'Berseem', 'Napier Grass', 'Maize Silage'] },
    { id: 'medicinal', name: 'Medicinal & Aromatic', examples: ['Aloe Vera', 'Tulsi', 'Ashwagandha', 'Lemongrass'] },
    { id: 'processed', name: 'Processed Agri', examples: ['Dehydrated Fruits', 'Fruit Pulp', 'Cold-Pressed Oils', 'Flour', 'Spice Powders'] },
  ];

  // UI state
  const [passCode, setPassCode] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(categories[0].id);
  const selectedCategory = useMemo(() => categories.find(c => c.id === selectedCategoryId), [selectedCategoryId]);
  const [selectedCommodity, setSelectedCommodity] = useState(selectedCategory.examples[0]);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Simulation state
  const [isLiveDataFeed, setIsLiveDataFeed] = useState(false);
  const [statusMsg, setStatusMsg] = useState('Idle');
  const [isLiveVideo, setIsLiveVideo] = useState(false);
  const [isShowGallery, setIsShowGallery] = useState(false);

  // Data streams (store last N samples)
  const MAX_SAMPLES = 30;
  const [timeSeries, setTimeSeries] = useState([]); // { t: Date, temp, humidity, moisture, stock, price }

  const intervalRef = useRef(null);

  // helper: random walk generator for nicer simulated data
  function randomWalk(prev, volatility=1, min=null, max=null) {
    const change = (Math.random() - 0.5) * volatility * 2;
    let nxt = (prev ?? 0) + change;
    if (min !== null) nxt = Math.max(min, nxt);
    if (max !== null) nxt = Math.min(max, nxt);
    return parseFloat(nxt.toFixed(2));
  }

  // initialize a seed based on commodity name so each commodity feels consistent
  function seedForCommodity(name) {
    let s = 0;
    for (let i = 0; i < name.length; i++) s += name.charCodeAt(i);
    return s % 100;
  }

  // create initial sample set
  function createInitialSeries(commodity) {
    const seed = seedForCommodity(commodity);
    // base values vary by category of commodity (simple heuristics)
    const base = {
      temp: 20 + (seed % 10),
      humidity: 50 + (seed % 20),
      moisture: 12 + (seed % 10),
      stock: 500 + (seed * 10),
      price: 50 + (seed % 30),
    };

    const now = Date.now();
    const arr = [];
    let last = { ...base };
    for (let i = MAX_SAMPLES - 1; i >= 0; i--) {
      // step back in time by 5s per sample
      const t = new Date(now - i * 5000);
      last = {
        temp: randomWalk(last.temp, 0.6, -10, 50),
        humidity: randomWalk(last.humidity, 1.2, 0, 100),
        moisture: randomWalk(last.moisture, 0.6, 0, 100),
        stock: Math.max(0, Math.round(randomWalk(last.stock, 25, 0, 100000))),
        price: Math.max(0.01, randomWalk(last.price, 0.8, 0.01, 100000)),
      };
      arr.push({ t, ...last });
    }
    return arr;
  }

  // when commodity or category changes, reset selected commodity and series
  useEffect(() => {
    setSelectedCommodity(selectedCategory.examples[0]);
  }, [selectedCategoryId]);

  // start simulation
  const startLiveDataFeed = () => {
    if (!passCode || passCode.trim().length < 4) {
      setStatusMsg('Enter valid Pass Code (min 4 chars)');
      return;
    }
    setStatusMsg('Starting live simulation...');
    const init = createInitialSeries(selectedCommodity);
    setTimeSeries(init);
    setIsLiveDataFeed(true);

    // clear existing interval if any
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setTimeSeries(prev => {
        const last = prev[prev.length - 1] || { temp: 25, humidity: 60, moisture: 12, stock: 500, price: 60 };
        const next = {
          t: new Date(),
          temp: randomWalk(last.temp, 0.8, -10, 50),
          humidity: randomWalk(last.humidity, 2.0, 0, 100),
          moisture: randomWalk(last.moisture, 1.0, 0, 100),
          stock: Math.max(0, Math.round(randomWalk(last.stock, 60, 0, 100000))),
          price: Math.max(0.01, randomWalk(last.price, 1.5, 0.01, 100000)),
        };
        const nextArr = [...prev.slice(-MAX_SAMPLES + 1), next];
        return nextArr;
      });
      setStatusMsg('Live — receiving data');
    }, 3000);
  };

  const stopLiveDataFeed = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsLiveDataFeed(false);
    setStatusMsg('Stopped');
  };

  const showLiveVideo = () => {
  };

  const showGallery = () => {
  };

  useEffect(() => {
    // cleanup on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // restart simulation when commodity changes while live
  useEffect(() => {
    if (isLiveDataFeed) {
      // re-initialize
      const init = createInitialSeries(selectedCommodity);
      setTimeSeries(init);
    }
  }, [selectedCommodity]);

  // derived latest values
  const latest = timeSeries[timeSeries.length - 1] || null;

  // mini sparkline generator (SVG polyline)
  function Sparkline({ data, height = 40 }) {
    if (!data || data.length === 0) return <div className="text-xs text-gray-400">no data</div>;
    const values = data.map(d => d);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = 4;
    const w = 140; // fixed tiny sparkline width
    const h = height;
    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={w} height={h} className="block">
        <polyline fill="none" strokeWidth="2" stroke="#16a34a" points={points} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  // prepare arrays for sparklines
  const tempSeries = timeSeries.map(s => s.temp);
  const humSeries = timeSeries.map(s => s.humidity);
  const moistSeries = timeSeries.map(s => s.moisture);
  const priceSeries = timeSeries.map(s => s.price);

  // small helper to format date/time
  const fmtTime = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    return dt.toLocaleTimeString();
  };

  return (
    <div className="min-h-screen bg-gray-500 p-4">
      <div className="mx-auto bg-gray-700 rounded-2xl shadow-md overflow-hidden">
        <div className="p-4">
          <h1 className="text-xl font-semibold">Digital Twin — AgriLink</h1>
          <p className="text-xs text-gray-50 mt-1">Realtime simulation · Connect with device using Pass Code</p>

          {/* Controls */}
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs">Digital Twin Pass Code</label>
              <input value={passCode} onChange={(e)=>setPassCode(e.target.value)} placeholder="Enter pass code" className="w-full p-2 rounded border" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs">Commodity Category</label>
                <select className="w-full h-10 p-2 rounded border" value={selectedCategoryId} onChange={(e)=>setSelectedCategoryId(e.target.value)}>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs">Commodity</label>
                <select className="w-full h-10 p-2 rounded border" value={selectedCommodity} onChange={(e)=>setSelectedCommodity(e.target.value)}>
                  {selectedCategory.examples.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button onClick={startLiveDataFeed} className={`px-4 py-2 rounded ${isLiveDataFeed ? 'bg-gray-300 text-gray-700' : 'bg-green-600 text-white'}`} disabled={isLiveDataFeed}>Live Data Feed</button>
              <button onClick={stopLiveDataFeed} className={`px-4 py-2 rounded ${isLiveDataFeed ? 'bg-red-600 text-white' : 'bg-red-500 text-gray-700'}`} disabled={!isLiveDataFeed}>Stop Feed</button>
              <button onClick={showLiveVideo} className={`px-4 py-2 rounded ${isLiveVideo ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`} disabled={!isLiveVideo}>Live Video</button>
              <button onClick={showGallery} className={`px-4 py-2 rounded ${isShowGallery ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`} disabled={!isShowGallery}>Image Gallery</button>
            </div>
            <div className="text-xs text-yellow-200 items-center">{statusMsg}</div>
          </div>

          {/* Live Dashboard */}
          <div className="mt-4">
            {/* Top cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">Temperature</div>
                <div className="text-lg font-semibold">{latest ? `${latest.temp} °C` : '--'}</div>
                <div className="mt-2"><Sparkline data={tempSeries} /></div>
              </div>

              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">Humidity</div>
                <div className="text-lg font-semibold">{latest ? `${latest.humidity} %` : '--'}</div>
                <div className="mt-2"><Sparkline data={humSeries} /></div>
              </div>

              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">Moisture</div>
                <div className="text-lg font-semibold">{latest ? `${latest.moisture} %` : '--'}</div>
                <div className="mt-2"><Sparkline data={moistSeries} /></div>
              </div>

              <div className="p-3 bg-gray-50 rounded">
                <div className="text-xs text-gray-500">Stock Level</div>
                <div className="text-lg font-semibold">{latest ? `${latest.stock}` : '--'}</div>
                <div className="text-xs text-gray-400 mt-1">units</div>
              </div>

              <div className="p-3 bg-gray-50 rounded sm:col-span-2">
                <div className="text-xs text-gray-500">Market Price</div>
                <div className="text-lg font-semibold">{latest ? `₹ ${latest.price.toFixed(2)}` : '--'}</div>
                <div className="mt-2"><Sparkline data={priceSeries} /></div>
              </div>
            </div>

            {/* Timeline / readings */}
            <div className="mt-4 bg-white p-3 rounded shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Recent Readings — {selectedCommodity}</div>
                <div className="text-xs text-gray-400">{timeSeries.length} samples</div>
              </div>

              <div className="mt-3 space-y-2 max-h-48 overflow-auto">
                {timeSeries.slice().reverse().map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                    <div>
                      <div className="font-medium">{fmtTime(s.t)}</div>
                      <div className="text-gray-500">Temp: {s.temp} °C • Humidity: {s.humidity}% • Moisture: {s.moisture}%</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">₹ {s.price.toFixed(2)}</div>
                      <div className="text-gray-500">Stock: {s.stock}</div>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Actions */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={() => { navigator.clipboard?.writeText(JSON.stringify({ passCode, category: selectedCategory.name, commodity: selectedCommodity })); alert('Twin share payload copied to clipboard (JSON)'); }}>Share Twin</button>
              <button className="px-3 py-2 rounded bg-gray-200 text-gray-700" onClick={()=>setShowAnalytics(true)}>Open Analytics</button>
            </div>

            {/* Show Analytics Modal */}
            { showAnalytics && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white p-4 rounded-xl max-w-md w-full relative">
                <button onClick={()=>setShowAnalytics(false)} className="absolute top-2 right-2 px-2 py-1 text-red-500 hover:text-gray-800 w-8">✕</button>
                  <AgriLinkAgriTwinAnalytics
                  commodity={selectedCommodity}
                  category={selectedCategory.name}
                  analyticsData={{
                  weeklyAvgTemp: 27,
                  weeklyAvgHumidity: 63,
                  moistureAlertCount: 2,
                  stockAlertCount: 1,
                  priceTrend: [52, 53.5, 51, 55, 54, 53, 56]
                  }}
                />
              </div>
            </div>
            )}
            {/* <div className="mt-4 text-xs text-gray-400">Note: This is a demo realtime simulation. Replace the simulator with your device/websocket feed or API for production.</div> */}
          </div>

        </div>
      </div>
    </div>
  );
}
